import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { useSEO } from '../hooks/useSEO';

const PressPage: React.FC = () => {
    const { t } = useTranslation();

    useSEO(
        t('pressPage_seo_title'),
        t('pressPage_seo_desc'),
        t('pressPage_seo_keywords').split(',').map(k => k.trim())
    );

    const pressFeatures = [
        {
            publication: "Vogue",
            quote: t('press_vogue_quote'),
            logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6c/Vogue_App_Logo.svg/100px-Vogue_App_Logo.svg.png"
        },
        {
            publication: "Harper's Bazaar",
            quote: t('press_harpers_quote'),
            logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d4/Harper%27s_Bazaar_logo.svg/200px-Harper%27s_Bazaar_logo.svg.png"
        },
        {
            publication: "Elle",
            quote: t('press_elle_quote'),
            logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e5/Elle_logo.svg/200px-Elle_logo.svg.png"
        }
    ];

    const collaborations = [
        { brand: "Brand1", season: "SS25", description: t('press_brand1_desc') },
        { brand: "Brand2", season: "FW24", description: t('press_brand2_desc') },
        { brand: "Brand3", season: "Resort 25", description: t('press_brand3_desc') }
    ];

    return (
        <div className="pt-24 pb-16 px-4 max-w-7xl mx-auto min-h-screen bg-white dark:bg-[#18181B] text-gray-900 dark:text-[#F1F1F1]">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-16"
            >
                <h1 className="text-5xl mb-6 text-gray-900 dark:text-[#F1F1F1] font-serif font-bold">{t('pressPage_title')}</h1>
                <p className="text-xl text-gray-600 dark:text-[#F1F1F1]/70 max-w-2xl mx-auto">
                    {t('pressPage_tagline')}
                </p>
            </motion.div>

            {/* As Seen In Section */}
            <section className="mb-24">
                <h2 className="text-3xl text-center mb-12 text-[#C0A172] uppercase tracking-widest text-sm font-sans font-bold">{t('pressPage_featured_title')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-center">
                    {pressFeatures.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: index * 0.2 }}
                            className="bg-gray-50 dark:bg-[#2A2A2A] p-8 rounded-2xl text-center h-full flex flex-col justify-center items-center shadow-sm hover:shadow-md transition-shadow border border-gray-100 dark:border-[#C0A172]/10"
                        >
                            <div className="h-16 flex items-center justify-center mb-6">
                                {/* Simulated Logo Placeholder if generic */}
                                <span className="font-serif text-3xl italic font-bold text-gray-800 dark:text-[#F1F1F1]">{feature.publication}</span>
                            </div>
                            <blockquote className="font-serif text-lg text-gray-700 dark:text-[#F1F1F1]/80 italic">"{feature.quote}"</blockquote>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Collaborations Section */}
            <section>
                <h2 className="text-3xl text-center mb-12 text-[#C0A172] uppercase tracking-widest text-sm font-sans font-bold">{t('pressPage_partners_title')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {collaborations.map((collab, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-white dark:bg-[#2A2A2A] border border-gray-100 dark:border-[#C0A172]/10 p-8 rounded-2xl hover:border-[#C0A172] transition-colors duration-300 group shadow-sm"
                        >
                            <h3 className="text-2xl mb-2 group-hover:text-[#C0A172] transition-colors font-serif font-bold dark:text-[#F1F1F1]">{collab.brand}</h3>
                            <span className="text-sm font-sans text-gray-500 dark:text-[#C0A172]/60 block mb-4 tracking-wider uppercase font-bold">{collab.season}</span>
                            <p className="text-gray-600 dark:text-[#F1F1F1]/70 font-serif leading-relaxed">{collab.description}</p>
                        </motion.div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default PressPage;
